import React from "react";
import logoMombasa from "../../../core/img/branch-logos/logo_mombasa.jpg";
import logoNairobi from "../../../core/img/branch-logos/logo_nairobi.jpg";
import logoSystems from "../../../core/img/branch-logos/logo_systems.jpg";
import logoTexastrack from "../../../core/img/branch-logos/logo_texastrack.png";

// Letterhead quotation format for Guards/Alarms-only enquiries - ported
// element-for-element from quotation_pdf_builder.dart's
// _buildGuardsAlarmsQuotation, matched against the real reference document
// (Working_Files/SALES_QUOT_FORMAT.pdf). Reuses the SAME branch logo assets
// already imported for LPO printing (StockDocumentPreview.jsx) rather than
// duplicating Flutter's separate copies under assets/images/quotation/ -
// one set of branch marks, not two.
// The Print button opens a bare window.open("", "_blank") (about:blank) and
// writes this component's innerHTML into it - a webpack-emitted root-
// relative logo path resolves fine in the live app but fails there (no
// valid base URL), same gotcha already fixed for StockDocumentPreview.jsx.
// Resolving to a full absolute URL up front makes both render paths (live
// preview/Send, and the Print popup) work identically.
const logoUrlFor = (branchCode) =>
  new URL(LOGO_BY_BRANCH_CODE[branchCode] || logoMombasa, window.location.origin).href;

const LOGO_BY_BRANCH_CODE = {
  MOMBASA: logoMombasa,
  NAIROBI: logoNairobi,
  SYSTEMS: logoSystems,
  TEXASTRACK: logoTexastrack,
};

const FONT_FAMILY = '-apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

const fmtKsh = (n) => Math.round(Number(n) || 0).toLocaleString("en-US");
const trimZeros = (n) => (Number.isInteger(Number(n)) ? String(Number(n)) : String(Number(n)));
const letter = (i) => `${String.fromCharCode(97 + i)})`;

const scheduleText = (schedule) => {
  switch (schedule) {
    case "WEEKENDS": return "weekends";
    case "CUSTOM_DATES": return "on custom dates";
    case "DAILY":
    default: return "daily";
  }
};

const ordinalDate = (date) => {
  const day = date.getDate();
  const suffix = day >= 11 && day <= 13 ? "TH" : { 1: "ST", 2: "ND", 3: "RD" }[day % 10] || "TH";
  const month = date.toLocaleDateString("en-US", { month: "long" }).toUpperCase();
  return `${day}${suffix} ${month} ${date.getFullYear()}`;
};

const LineItem = ({ letter: l, description, amount, subNote }) => (
  <div style={{ marginBottom: 6 }}>
    <div style={{ display: "flex", alignItems: "flex-start" }}>
      <div style={{ width: 20, fontWeight: "bold", fontSize: 10 }}>{l}</div>
      <div style={{ flex: 1, fontWeight: "bold", fontSize: 10 }}>{description}</div>
      <div style={{ marginLeft: 8, fontSize: 10 }}>{amount}</div>
    </div>
    {subNote && <div style={{ marginLeft: 20, marginTop: 2, fontSize: 10 }}>{subNote}</div>}
  </div>
);

// discountAmount/discountLabel are only rendered when there is a real
// discount, so an undiscounted quotation prints exactly as it always did.
// The discount reduces the VAT-able value (the caller computes VAT on the
// net), which is the decision taken with the user: VAT is only declared on
// what is actually billed.
const TotalsBox = ({ subTotal, vatRate, vatAmount, total, discountAmount = 0, discountLabel = "DISCOUNT" }) => (
  <div style={{ display: "flex", justifyContent: "flex-end" }}>
    <table style={{ borderCollapse: "collapse", border: "0.7px solid #000" }}>
      <tbody>
        <tr>
          <td style={{ border: "0.7px solid #000", padding: "4px 6px", width: 120, fontSize: 10 }}>SUB TOTAL</td>
          <td style={{ border: "0.7px solid #000", padding: "4px 6px", width: 110, fontSize: 10 }}>{fmtKsh(subTotal)}/-</td>
        </tr>
        {discountAmount > 0 && (
          <>
            <tr>
              <td style={{ border: "0.7px solid #000", padding: "4px 6px", fontSize: 10 }}>{discountLabel}</td>
              <td style={{ border: "0.7px solid #000", padding: "4px 6px", fontSize: 10 }}>-{fmtKsh(discountAmount)}/-</td>
            </tr>
            <tr>
              <td style={{ border: "0.7px solid #000", padding: "4px 6px", fontSize: 10 }}>NET</td>
              <td style={{ border: "0.7px solid #000", padding: "4px 6px", fontSize: 10 }}>{fmtKsh(subTotal - discountAmount)}/-</td>
            </tr>
          </>
        )}
        <tr>
          <td style={{ border: "0.7px solid #000", padding: "4px 6px", fontSize: 10 }}>VAT {trimZeros(vatRate)}%</td>
          <td style={{ border: "0.7px solid #000", padding: "4px 6px", fontSize: 10 }}>{fmtKsh(vatAmount)}/-</td>
        </tr>
        <tr>
          <td style={{ border: "0.7px solid #000", padding: "4px 6px", fontSize: 10, fontWeight: "bold" }}>MONTHLY TOTAL</td>
          <td style={{ border: "0.7px solid #000", padding: "4px 6px", fontSize: 10, fontWeight: "bold" }}>{fmtKsh(total)}/-</td>
        </tr>
      </tbody>
    </table>
  </div>
);

const GuardsAlarmsQuotationPreview = ({ enquiry, guardRequirements = [], company }) => {
  const hasGuards = (enquiry.guardsCount || 0) > 0;
  const hasAlarms = (enquiry.alarmsCount || 0) > 0;
  const vatRate = enquiry.vatRate || 0;

  const guardsRate = enquiry.guardsRate || 0;
  const guardsAmount = enquiry.guardsAmount || 0;

  const alarmsCount = enquiry.alarmsCount || 0;
  const alarmsRate = enquiry.alarmsRate || 0;
  const alarmsAmount = alarmsCount * alarmsRate;

  // The discount is a single enquiry-level figure, but this quotation prints a
  // separate totals box per section. Apportion it by each section's share of
  // the sub-total so the sections still add up to the enquiry's own total -
  // with only one section priced (the common case) the whole discount lands
  // there, which is exactly what you want.
  const discountAmount = enquiry.discountAmount || 0;
  const discountBase = guardsAmount + alarmsAmount;
  const shareOfDiscount = (sectionAmount) =>
    discountBase > 0 ? (discountAmount * sectionAmount) / discountBase : 0;
  // Print "DISCOUNT (10%)" when it was entered as a percentage, a plain
  // "DISCOUNT" line when a flat amount was typed.
  const discountLabel = enquiry.discountPercent ? `DISCOUNT (${trimZeros(enquiry.discountPercent)}%)` : "DISCOUNT";

  const guardsDiscount = shareOfDiscount(guardsAmount);
  const guardsNet = guardsAmount - guardsDiscount;
  const guardsVat = (guardsNet * vatRate) / 100;
  const guardsMonthlyTotal = guardsNet + guardsVat;

  const alarmsDiscount = shareOfDiscount(alarmsAmount);
  const alarmsNet = alarmsAmount - alarmsDiscount;
  const alarmsVat = (alarmsNet * vatRate) / 100;
  const alarmsMonthlyTotal = alarmsNet + alarmsVat;

  return (
    <div style={{ fontFamily: FONT_FAMILY, fontSize: 11, lineHeight: 1.3, color: "#000", background: "#fff", padding: 24 }}>
      <div style={{ textAlign: "center" }}>
        <img src={logoUrlFor(enquiry.branchCode)} alt={company?.name || "Texas Alarms"} style={{ height: 85 }} />
      </div>
      <div style={{ marginTop: 10, background: "#FFF200", padding: "8px 12px", textAlign: "center" }}>
        <div style={{ fontSize: 10, fontWeight: "bold", fontStyle: "italic" }}>
          Manual Alarm, Automatic Alarm, Electric Fence, Alarm Response,
          <br />
          Security Guards, CCTV &amp; Access Control
        </div>
      </div>
      <div style={{ marginTop: 22, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: "bold" }}>{(enquiry.customerName || "Customer").toUpperCase()}</div>
          {enquiry.customerAddress && <div style={{ fontSize: 10 }}>{enquiry.customerAddress}</div>}
          {enquiry.area && <div style={{ fontSize: 10 }}>{enquiry.area}</div>}
        </div>
        {/* Right column: date, then who to call about this quote. The customer
            gets a named person and a number rather than a general line.
            Mobile comes from the salesperson's linked employee record; a
            service account with no employee link prints the name alone rather
            than an empty label. */}
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 10, fontWeight: "bold" }}>{ordinalDate(new Date())}</div>
          {enquiry.salespersonName && (
            <div style={{ marginTop: 6, fontSize: 10, lineHeight: 1.35 }}>
              <div style={{ fontWeight: "bold" }}>{enquiry.salespersonName}</div>
              {enquiry.salespersonMobile && <div>{enquiry.salespersonMobile}</div>}
            </div>
          )}
        </div>
      </div>
      <div style={{ marginTop: 18, fontSize: 10 }}>Dear Sir,</div>
      <div style={{ marginTop: 10, fontSize: 10, fontWeight: "bold", textDecoration: "underline" }}>
        RE: QUOTATION FOR GENERAL SECURITY SERVICES.
      </div>
      <div style={{ marginTop: 6, fontSize: 10 }}>
        Further reference to the above subject, we wish to submit our above-mentioned quotation for your kind perusal
        and subsequent action as appended here below. Please find our Best rates as follows: -
      </div>

      {hasGuards && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 11, fontWeight: "bold", textDecoration: "underline" }}>GUARDING</div>
          <div style={{ marginTop: 8 }}>
            {guardRequirements.length === 0 ? (
              <LineItem
                letter="a)"
                description={`${enquiry.guardsCount} Guard${enquiry.guardsCount === 1 ? "" : "s"} working 12 hours shift per day, per month.`}
                amount={`${fmtKsh(guardsAmount)}/-+VAT`}
                subNote={`(@ ${fmtKsh(guardsRate)}/-+VAT per Guard).`}
              />
            ) : (
              guardRequirements.map((line, i) => {
                const effectiveRate = Number(line.rate) || guardsRate;
                const amount = (Number(line.qty) || 0) * effectiveRate;
                const shift = line.shift === "DAY" ? "Day" : "Night";
                const term =
                  line.deploymentType === "PERMANENT"
                    ? "working 12 hours shift per day, per month"
                    : `on a temporary basis (${line.startDate} to ${line.endDate || "-"})`;
                return (
                  <LineItem
                    key={line.lineNumber ?? i}
                    letter={letter(i)}
                    description={`${line.qty} Guard${Number(line.qty) === 1 ? "" : "s"} (${shift}) ${term}, ${scheduleText(line.schedule)}.`}
                    amount={`${fmtKsh(amount)}/-+VAT`}
                    subNote={`(@ ${fmtKsh(effectiveRate)}/-+VAT per Guard).`}
                  />
                );
              })
            )}
          </div>
          <div style={{ marginTop: 10 }}>
            <TotalsBox subTotal={guardsAmount} vatRate={vatRate} vatAmount={guardsVat} total={guardsMonthlyTotal} discountAmount={guardsDiscount} discountLabel={discountLabel} />
          </div>
        </div>
      )}

      {hasAlarms && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 11, fontWeight: "bold", textDecoration: "underline" }}>ALARM RESPONSE</div>
          <div style={{ marginTop: 8 }}>
            <LineItem letter="a)" description={`Monthly Alarm Back Up Response (For ${alarmsCount} Transmitter${alarmsCount === 1 ? "" : "s"} Only).`} amount={`${fmtKsh(alarmsAmount)}/-+VAT`} />
            <LineItem letter="b)" description="Yearly Telkom (C.A.K) License (One Time payment per Year)." amount={`Kshs ${fmtKsh(1425 * alarmsCount)}/-`} />
            <LineItem letter="c)" description="Remote Kit - Optional (One-Time Payment)" amount="Kshs 8,000/-+VAT" />
            <LineItem letter="d)" description="Monthly Maintenance" amount="Free" />
            <LineItem letter="e)" description="Installation of Transmitter" amount="Free" />
            <LineItem letter="f)" description="3 Panic Switches" amount="Free" />
          </div>
          <div style={{ marginTop: 10 }}>
            <TotalsBox subTotal={alarmsAmount} vatRate={vatRate} vatAmount={alarmsVat} total={alarmsMonthlyTotal} discountAmount={alarmsDiscount} discountLabel={discountLabel} />
          </div>
        </div>
      )}

      <div style={{ marginTop: 18, fontSize: 10 }}>Anticipating your favorable response and looking forward to do business with you.</div>
      <div style={{ marginTop: 18, fontSize: 10 }}>Yours faithfully,</div>
      <div style={{ fontSize: 10 }}>For {company?.name || "Texas Alarms (K) Limited"}</div>
      <div style={{ marginTop: 10, fontSize: 9, fontStyle: "italic", color: "#666" }}>
        This is a digitally generated quotation and does not require a physical signature.
      </div>
      <div style={{ marginTop: 10, fontSize: 10, fontWeight: "bold" }}>General Manager</div>
      <div style={{ marginTop: 18, fontSize: 10 }}>For further queries, kindly contact:</div>
      {company?.phone && <div style={{ fontSize: 10, fontWeight: "bold" }}>{company.phone}</div>}
    </div>
  );
};

export default GuardsAlarmsQuotationPreview;
